import { useMutation } from '@apollo/client';
import { updateExperimentVariantMutation } from 'api/mutations/experiments';
import { updateExperimentEditVariants } from 'modules/experiments/takeover/ducks/actions';
import { MutatedExperimentVariant } from 'modules/experiments/takeover/types';
import { MutateVariant, MutateVariantResponse, UpdateVariantArgs } from 'modules/experiments/types';
import { mutateEditExperimentVariantState } from 'modules/experiments/utils';
import { addNegativeToast, errorIdentifier } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { useExperimentsTranslations } from '../useTranslations';

const source = 'useUpdateExperimentVariant';

export const useUpdateExperimentVariant = ({
  experimentId,
  showErrorToaster = true,
}: MutateVariant) => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const errorMessage = `Failed to update variant for experiment id ${experimentId}`;
  const errorToasterMessage = t('errorMessage.updateVariant');

  const errorHandler = (error: unknown) => {
    if (showErrorToaster)
      dispatch(
        addNegativeToast(
          errorToasterMessage,
          errorIdentifier({
            errorMessage,
            source,
            error,
          }),
        ),
      );
  };

  const [updateVariation] = useMutation(updateExperimentVariantMutation, {
    onError: errorHandler,
  });

  return async (
    updateVariantArgs: UpdateVariantArgs,
    updateRedux = true,
  ): Promise<MutateVariantResponse> => {
    const { variationId, variationName, locale, variationType, ...variantInput } =
      updateVariantArgs;

    const { data } = await updateVariation({
      variables: {
        experimentId,
        variationId,
        variationName,
        ...(locale && { locale }),
        variationType,
        ...variantInput,
      },
    });

    if (data?.updateVariation?.variations) {
      const {
        updateVariation: { variations },
      } = data;

      // We need to save the original BE variant object to the redux state
      // We have selectors which will mutate this date when a component needs it
      if (updateRedux) {
        dispatch(updateExperimentEditVariants(variations));
      }

      const mutatedVariants = mutateEditExperimentVariantState(variations);
      return {
        isSuccess: true,
        mutatedVariant:
          mutatedVariants.find(({ id }) => id === variationId) || ({} as MutatedExperimentVariant),
      };
    }

    if (data?.updateVariation?.message) {
      errorHandler(data.updateVariation.message);
    }
    return {
      isSuccess: false,
    };
  };
};
